import React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLocalization } from "@/lib/localization";
import { cn } from "@/lib/utils";

const invoiceFieldClassName = "h-10 text-[12px] focus-visible:ring-0 focus-visible:ring-transparent";

function InvoiceField({ children, className, label }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label className="text-[12px] font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function InvoiceInput({ className, ...props }) {
  return <Input className={cn(invoiceFieldClassName, className)} {...props} />;
}

function InvoiceDatePicker({ className, onChange, placeholder = "dd/mm/yyyy", value, ...props }) {
  const { messages } = useLocalization();
  const selectedDate = value ? new Date(`${value}T00:00:00`) : undefined;
  const resolvedPlaceholder = placeholder === "dd/mm/yyyy" ? messages.common.datePlaceholder : placeholder;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            invoiceFieldClassName,
            "w-full justify-between px-3 font-normal shadow-sm hover:bg-background hover:text-foreground",
            !value && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <span>{selectedDate ? format(selectedDate, "dd/MM/yyyy") : resolvedPlaceholder}</span>
          <CalendarIcon className="h-4 w-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[120] w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => onChange(date ? format(date, "yyyy-MM-dd") : "")}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export { InvoiceDatePicker, InvoiceField, InvoiceInput };
